import React from "react";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, ChevronDown, ChevronRight } from "lucide-react";

interface TableRowProps {
  level: number;
  name: string;
  currentValue: number;
  comparisonValue: number;
  isExpandable?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  transactionCount?: number;
  icon?: React.ReactNode;
  categoryKey?: string;
  getCategoryBasedValueColor: (value: number, category?: string) => string;
}

export const TableRow: React.FC<TableRowProps> = ({
  level,
  name,
  currentValue,
  comparisonValue,
  isExpandable = false,
  isExpanded = false,
  onToggle,
  transactionCount,
  icon,
  categoryKey,
  getCategoryBasedValueColor,
}) => {

  const formatValue = (value: number) => {
    const absValue = Math.abs(value);
    const sign = value >= 0 ? "" : "-";
    return `${sign}$${absValue.toLocaleString()}`;
  };

  const getChangeIndicator = () => {
    if (comparisonValue === 0 && currentValue === 0) return null;
    
    const change = currentValue - comparisonValue;
    const changePercent = comparisonValue !== 0 ? Math.abs((change / comparisonValue) * 100) : 0;
    
    // Only show indicator for significant changes (>1% or >$100)
    if (Math.abs(change) < 100 && changePercent < 1) return null;
    
    if (comparisonValue === 0 && currentValue !== 0) {
      return (
        <div className="flex items-center gap-1">
          <TrendingUp className="h-4 w-4 text-blue-600" />
          <span className="text-xs text-blue-600 font-medium">NEW</span>
        </div>
      );
    }
    
    if (change > 0) {
      return (
        <div className="flex items-center gap-1">
          <TrendingUp className="h-4 w-4 text-emerald-600" />
          <span className="text-xs text-emerald-600 font-medium">
            +${Math.abs(change).toLocaleString()}
          </span>
        </div>
      );
    }
    
    if (change < 0) {
      return (
        <div className="flex items-center gap-1">
          <TrendingDown className="h-4 w-4 text-red-600" />
          <span className="text-xs text-red-600 font-medium">
            -${Math.abs(change).toLocaleString()}
          </span>
        </div>
      );
    }
    
    return null;
  };

  // Enhanced row styling based on level and change significance
  const getRowStyling = () => {
    const change = currentValue - comparisonValue;
    const isSignificantChange = Math.abs(change) >= 1000;
    
    let baseClasses = "border-b hover:bg-gray-50 transition-colors";
    
    if (level === 0) {
      baseClasses += " bg-blue-50 border-blue-200";
    } else if (level === 1) {
      baseClasses += " bg-gray-25";
      if (isSignificantChange) {
        baseClasses += change > 0 ? " border-l-4 border-l-emerald-400" : " border-l-4 border-l-red-400";
      }
    }
    
    return baseClasses;
  };

  return (
    <tr className={getRowStyling()}>
      <td className="py-3 px-4">
        <div 
          className={`flex items-center gap-2`}
          style={{ marginLeft: `${level * 20}px` }}
        >
          {isExpandable && (
            <button onClick={onToggle} className="p-1 hover:bg-gray-200 rounded transition-colors">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-600" />
              )}
            </button>
          )}
          {icon}
          <span className={`${level === 0 ? 'font-bold text-lg text-blue-900' : level === 1 ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>
            {name}
          </span>
          {transactionCount && (
            <Badge variant="secondary" className="ml-2 bg-gray-100 text-gray-700">
              {transactionCount} txns
            </Badge>
          )}
        </div>
      </td>
      <td className={`py-3 px-4 text-right ${getCategoryBasedValueColor(currentValue, categoryKey)}`}>
        <div className="flex items-center justify-end gap-3">
          <span className="font-mono">{formatValue(currentValue)}</span>
          {getChangeIndicator()}
        </div>
      </td>
      <td className={`py-3 px-4 text-right ${getCategoryBasedValueColor(comparisonValue, categoryKey)} opacity-75`}>
        <div className="flex flex-col items-end gap-0.5">
          <span className="font-mono">{formatValue(comparisonValue)}</span>
          {(() => {
            const change = currentValue - comparisonValue;
            if (change === 0 || comparisonValue === 0) return null;
            
            const percentChange = ((change / Math.abs(comparisonValue)) * 100);
            const changeColor = change > 0 ? 'text-emerald-600' : 'text-red-600';
            
            return (
              <span className={`text-xs font-medium ${changeColor} opacity-75`}>
                {change > 0 ? '+' : ''}{percentChange.toFixed(1)}%
              </span>
            );
          })()}
        </div>
      </td>
      <td className="py-3 px-4 text-right">
        {(() => {
          const change = currentValue - comparisonValue;
          if (change === 0) return <span className="text-gray-500 font-mono">—</span>;
          
          const changeColor = change > 0 ? 'text-emerald-700' : 'text-red-700';
          const changeIcon = change > 0 ? 
            <TrendingUp className="h-3 w-3 text-emerald-600" /> : 
            <TrendingDown className="h-3 w-3 text-red-600" />;
          
          // Calculate percentage change
          const percentChange = comparisonValue !== 0 ? ((change / Math.abs(comparisonValue)) * 100) : null;
          const percentDisplay = percentChange !== null ? 
            `(${percentChange > 0 ? '+' : ''}${percentChange.toFixed(1)}%)` : 
            '(New)';
          
          return (
            <div className="flex flex-col items-end gap-0.5">
              <div className="flex items-center gap-1">
                {changeIcon}
                <span className={`font-mono font-semibold ${changeColor}`}>
                  {change > 0 ? '+' : ''}{formatValue(change)}
                </span>
              </div>
              <span className={`text-xs font-medium ${changeColor} opacity-75`}>
                {percentDisplay}
              </span>
            </div>
          );
        })()}
      </td>
    </tr>
  );
}; 